type TooltipLineGenerator = (metadata: CatalogMetadata) => string | null;

/**
 * Helper function to safely format dates.
 * This remains a file-level pure function, as it doesn't need to be part of the class.
 * @param value The date value (Date object or string).
 * @returns A locale-specific date string or null if conversion fails.
 */
const safeFormatDate = (value: any): string | null => {
    if (!value) return null;
    try {
        const date = value instanceof Date ? value : new Date(value);
        // Check if the date is valid before formatting
        return !isNaN(date.getTime()) ? date.toLocaleString() : null;
    } catch {
        return null;
    }
};

export class CatalogMetadata {
    // Properties
    createdBy: string;
    createdOn: Date;
    updatedBy?: string;
    updatedOn?: Date;
    designId?: string;
    designName?: string;
    notes?: string;

    private static readonly tooltipLineGenerators: TooltipLineGenerator[] = [
        (m) => {
            const formattedDate = safeFormatDate(m.createdOn);
            return m.createdBy ? `Created: ${m.createdBy} on ${formattedDate}`  : null;
        },
        (m) => {
            const formattedDate = safeFormatDate(m.updatedOn);
            return m.updatedBy ? `Updated: ${m.updatedBy} on ${formattedDate}`  : null;
        },
        (m) => m.designName ? `Source Design: ${m.designName}` : null,
        (m) => m.notes ? `${m.notes}` : null, 
    ];

    constructor(init?: Partial<CatalogMetadata>) {
        // Use Object.assign for basic property copying
        Object.assign(this, init);

        if (init?.createdOn && !(init.createdOn instanceof Date)) {
            this.createdOn = new Date(init.createdOn);
        }
        if (init?.updatedOn && !(init.updatedOn instanceof Date)) {
            this.updatedOn = new Date(init.updatedOn);
        }
    }

    /**
     * Generates the tooltip string for THIS instance of metadata.
     * It is now an instance method with no arguments.
     * @returns The generated tooltip string.
     */
    toTooltip(): string {

        const lines = CatalogMetadata.tooltipLineGenerators
            .map(generator => generator(this))
            .filter((line): line is string => line !== null);

        const result = lines.join('\n');

        return result.length > 0 ? result : 'No metadata available';
    }

}